import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Info } from 'lucide-react';
import type { Lender, LenderUser, LenderUserDetail, UpdateLenderUserRequest } from '../../api/lenders';
import { useUpdateLenderUser, useLenderUser, useLenderUserCascades } from '../../hooks/useLenders';
import { useProducts } from '../../hooks/useProducts';
import ProductAccordionTree from './wizard/components/ProductAccordionTree';
import WizardDialog from './wizard/components/WizardDialog';
import { buildProductTree } from './wizard/components/productTree';
import { buildUserSchema, type UserFormValues } from './wizard/schemas/user.schema';
import { USER_ROLE_LABELS } from './wizard/types';
import {
  DISCARD_ACCESS_BODY,
  DISCARD_ACCESS_CANCEL,
  DISCARD_ACCESS_CONFIRM,
  DISCARD_ACCESS_TITLE,
  ACCESS_HINT,
  ACCESS_ON_TEXT,
  EDIT_ACCESS_LABEL,
} from './wizard/copy';
import closeIcon from './assets/product-close.svg';
import toggleOff from './assets/product-toggle-off.svg';
import toggleOn from './assets/product-toggle-on.svg';

const SAVE_FAILED = 'Unable to save the user. Please try again.';
const LOAD_FAILED = 'Unable to load this user. Please try again.';

// Same shape as Add User; the MeridianLink user names are optional here too.
const schema = buildUserSchema({ requireMlConsumerUserName: false });

/**
 * A LOS username to send, or undefined to leave it alone — only what the operator actually
 * changed travels.
 *
 * Two reasons not to send it on every save. The drawer shows the field whenever the lender has
 * the integration, but the backend resolves it by `lenderCode`, and the wizard can write
 * `meridianlink: { baseApiUrl }` with no code at all; a username for an integration it cannot
 * resolve is a 400, which would fail a save that only touched the name. And an empty string is
 * a real value here — it removes the user's login on that LOS — so it cannot be filtered out
 * the way a blank optional field usually is.
 */
const changedLogin = (value: string | undefined, stored: string | undefined): string | undefined => {
  const next = (value ?? '').trim();
  return next === (stored ?? '').trim() ? undefined : next;
};

const lenderProductUrns = (lender: Lender): string[] =>
  (lender.products ?? []).map((product) => (typeof product === 'string' ? product : product.urn));

interface EditLenderUserDrawerProps {
  lender: Lender;
  user: LenderUser;
  onClose: () => void;
  onSaved: (user: LenderUserDetail) => void;
}

/**
 * The console's Edit User (OM-4023), with the per-user Product Access panel inside it (OM-4022).
 *
 * The shell fetches the user before the form exists so the form can seed straight from stored
 * state: whether the user inherits the lender's catalog is the server's `productAccess` being
 * null, never something inferred from which products happen to be materialized on the row.
 */
export default function EditLenderUserDrawer({
  lender, user, onClose, onSaved,
}: Readonly<EditLenderUserDrawerProps>) {
  const { data: detail, isLoading, isError } = useLenderUser(lender.urn, user.urn);

  return (
    <div className="fixed inset-0 z-50">
      {/* A real button rather than a div carrying a click handler — a click target has to be
          operable, and the native element brings that with it. Kept out of the tab order: a
          full-viewport button is not somewhere to land, and the labelled close button above is
          the keyboard path to the same action. */}
      <button
        type="button"
        aria-label="Dismiss edit user"
        tabIndex={-1}
        onClick={onClose}
        className="absolute inset-x-0 bottom-0 top-[60px] w-full cursor-default bg-black/40"
      />
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-lender-user-title"
        className="absolute bottom-0 right-0 top-[60px] w-full max-w-[668px] border border-[#ddd] bg-surface shadow-lg dark:border-border-subtle"
      >
        <button
          type="button"
          aria-label="Close edit user"
          onClick={onClose}
          className="absolute right-6 top-[22px] z-10"
        >
          <img src={closeIcon} alt="" />
        </button>
        {isLoading && (
          <p className="px-6 pt-[22px] text-content-secondary sm:px-10">Loading…</p>
        )}
        {isError && (
          <p role="alert" className="px-6 pt-[22px] text-sm text-red-600 sm:px-10">{LOAD_FAILED}</p>
        )}
        {detail && (
          <EditLenderUserForm lender={lender} detail={detail} onClose={onClose} onSaved={onSaved} />
        )}
      </section>
    </div>
  );
}

interface EditLenderUserFormProps {
  lender: Lender;
  detail: LenderUserDetail;
  onClose: () => void;
  onSaved: (user: LenderUserDetail) => void;
}

function EditLenderUserForm({ lender, detail, onClose, onSaved }: Readonly<EditLenderUserFormProps>) {
  const updateUser = useUpdateLenderUser();
  const { data: catalog } = useProducts();
  const { data: cascades } = useLenderUserCascades(lender.urn, detail.urn);
  const tree = useMemo(() => buildProductTree(catalog ?? []), [catalog]);
  const lenderProducts = useMemo(() => lenderProductUrns(lender), [lender]);

  const hasMlConsumer = Boolean(lender.integrations?.meridianlink);
  const hasMlMortgage = Boolean(lender.integrations?.['meridianlink-mortgage']);

  // null = the user inherits every product of the lender, including ones added later; a record
  // restricts them to the urns set true, so {} means access to nothing. Straight from the server.
  const [productAccess, setProductAccess] = useState<Record<string, boolean> | null>(detail.productAccess);
  // Only the cascades the operator actually changed are sent, so an untouched section writes
  // nothing — and a cascade list that failed to load cannot revoke anything.
  const [cascadeChanges, setCascadeChanges] = useState<Record<string, boolean>>({});
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { register, handleSubmit, formState: { errors, isValid } } = useForm<UserFormValues>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: {
      firstName: detail.firstName ?? '',
      lastName: detail.lastName ?? '',
      email: detail.email,
      role: (detail.role as UserFormValues['role']) ?? 'lender-standard',
      mlConsumerUserName: detail.mlConsumerUserName ?? '',
      mlMortgageUserName: detail.mlMortgageUserName ?? '',
    },
  });

  const cascadeLeaves = useMemo(
    () => (cascades ?? []).map((cascade) => ({ id: cascade.id, name: cascade.name })),
    [cascades],
  );
  const cascadesSelected = useMemo(() => {
    const stored = Object.fromEntries((cascades ?? []).map((cascade) => [cascade.id, cascade.granted]));
    return { ...stored, ...cascadeChanges };
  }, [cascades, cascadeChanges]);

  const canSave = isValid && !updateUser.isPending;

  const toggleAccess = () => {
    // Off is always confirmed: it reverts the user to inheriting everything.
    if (productAccess !== null) {
      setConfirmingDiscard(true);
    } else {
      setProductAccess(Object.fromEntries(lenderProducts.map((urn) => [urn, true])));
    }
  };

  const toggleProduct = (urn: string) => {
    setProductAccess((current) => (current === null ? current : { ...current, [urn]: !current[urn] }));
  };

  const toggleCascade = (id: string) => {
    setCascadeChanges((current) => ({ ...current, [id]: !cascadesSelected[id] }));
  };

  const submit = handleSubmit(async (values) => {
    setError(null);
    const data: UpdateLenderUserRequest = {
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      role: values.role,
      productAccess,
    };
    // Only for a LOS the lender actually has, and only when it changed — an absent field leaves
    // whatever is stored, an empty one removes the login.
    const mlConsumer = hasMlConsumer
      ? changedLogin(values.mlConsumerUserName, detail.mlConsumerUserName)
      : undefined;
    if (mlConsumer !== undefined) data.mlConsumerUserName = mlConsumer;
    const mlMortgage = hasMlMortgage
      ? changedLogin(values.mlMortgageUserName, detail.mlMortgageUserName)
      : undefined;
    if (mlMortgage !== undefined) data.mlMortgageUserName = mlMortgage;
    if (Object.keys(cascadeChanges).length > 0) data.cascadeAccess = cascadeChanges;
    try {
      const saved = await updateUser.mutateAsync({ urn: lender.urn, userUrn: detail.urn, data });
      onSaved(saved);
    } catch (cause) {
      // 409 is the one failure that names something the operator can act on: which MeridianLink
      // username is taken, and on which of the two sides. Everything else gets the generic copy.
      const response = (cause as { response?: { status?: number; data?: { message?: string } } })
        ?.response;
      setError(
        (response?.status === 409 && response.data?.message) || SAVE_FAILED,
      );
    }
  });

  const accessOn = productAccess !== null;

  return (
    <form onSubmit={submit} className="flex h-full min-h-0 flex-col px-6 pb-6 pt-[22px] sm:px-10 sm:pb-14">
      <h2 id="edit-lender-user-title" className="shrink-0 text-[21px] font-medium leading-8 text-content">
        Edit User
      </h2>
      <div className="mt-5 min-h-0 flex-1 space-y-5 overflow-y-auto">
        <section className="space-y-3">
          <h3 className="text-[16px] font-medium leading-6 text-content">User Info</h3>
          <div className="grid grid-cols-1 gap-x-2 gap-y-3 sm:grid-cols-2">
            <div className="field">
              <label className="label" htmlFor="edit-user-first-name">
                First Name <span className="text-red-500">*</span>
              </label>
              <input id="edit-user-first-name" className="input" {...register('firstName')} />
              {errors.firstName && <p className="mt-1 text-sm text-red-500">{errors.firstName.message}</p>}
            </div>
            <div className="field">
              <label className="label" htmlFor="edit-user-last-name">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input id="edit-user-last-name" className="input" {...register('lastName')} />
              {errors.lastName && <p className="mt-1 text-sm text-red-500">{errors.lastName.message}</p>}
            </div>
            <div className="field">
              <label className="label" htmlFor="edit-user-email">Email</label>
              {/* Read-only: the address is half of the user's urn and of every LOS identity
                  built from it, so editing it here would orphan the inbound routing. */}
              <input id="edit-user-email" className="input" readOnly {...register('email')} />
            </div>
            <div className="field">
              <label className="label" htmlFor="edit-user-role">Role</label>
              <select id="edit-user-role" className="input" {...register('role')}>
                {Object.entries(USER_ROLE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            {hasMlConsumer && (
              <div className="field">
                <label className="label" htmlFor="edit-user-ml-consumer">MeridianLink Consumer Username</label>
                <input id="edit-user-ml-consumer" className="input" {...register('mlConsumerUserName')} />
              </div>
            )}
            {hasMlMortgage && (
              <div className="field">
                <label className="label" htmlFor="edit-user-ml-mortgage">MeridianLink Mortgage Username</label>
                <input id="edit-user-ml-mortgage" className="input" {...register('mlMortgageUserName')} />
              </div>
            )}
          </div>
        </section>

        <section className="space-y-3">
          <h3 className="text-[16px] font-medium leading-6 text-content">Product Access</h3>
          <p className="text-xs leading-[18px] text-content-secondary">{ACCESS_HINT}</p>
          <button
            type="button"
            role="switch"
            aria-checked={accessOn}
            aria-label={EDIT_ACCESS_LABEL}
            onClick={toggleAccess}
            className="flex items-center gap-4 text-sm text-content-secondary"
          >
            <img src={accessOn ? toggleOn : toggleOff} alt="" />
            {EDIT_ACCESS_LABEL}
          </button>
          {accessOn && (
            <>
              <div className="flex items-start gap-3 rounded-lg border border-brand bg-brand-tint px-4 py-3 text-sm text-brand-deep dark:text-content">
                <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                <p>{ACCESS_ON_TEXT}</p>
              </div>
              <ProductAccordionTree
                tree={tree}
                selected={productAccess ?? {}}
                onToggle={toggleProduct}
                visibleUrns={lenderProducts}
                cascades={cascadeLeaves}
                cascadesSelected={cascadesSelected}
                onToggleCascade={toggleCascade}
              />
            </>
          )}
        </section>

        {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
      </div>
      <div className="mt-8 flex shrink-0 items-center justify-between gap-4">
        <button
          type="button"
          onClick={onClose}
          disabled={updateUser.isPending}
          className="h-11 w-[154px] rounded-lg border border-brand bg-surface text-base font-semibold text-content disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!canSave}
          className="h-11 w-[151px] rounded-lg bg-brand text-base font-semibold text-white disabled:opacity-20"
        >
          Save Changes
        </button>
      </div>

      <WizardDialog
        open={confirmingDiscard}
        title={DISCARD_ACCESS_TITLE}
        body={DISCARD_ACCESS_BODY}
        confirmLabel={DISCARD_ACCESS_CONFIRM}
        cancelLabel={DISCARD_ACCESS_CANCEL}
        // Pending cascade edits go with it. Switching access off hands the user every cascade of
        // the lender, server-side, and the backend rejects the whole save with a 400 if a
        // `false` arrives for a user that inherits — so a cascade turned off a moment ago must
        // not survive the toggle. The section is hidden from here on anyway.
        onConfirm={() => {
          setConfirmingDiscard(false);
          setProductAccess(null);
          setCascadeChanges({});
        }}
        onCancel={() => setConfirmingDiscard(false)}
      />
    </form>
  );
}
